import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import PdfViewerWithComments from '../components/PdfViewerWithComments';
import CommentList from '../components/CommentList';
import useReview from '../hooks/useReview';
import './CommentPage.css';
import HelpButton from '../components/HelpButton';
import { saveReview } from '../api/reviewService';
import useUpdateBreadcrumbs from '../hooks/useUpdateBreadcrumbs';
import api from '../api/axios';
import { UserContext } from '../contexts/UserContext';
import useLocale from '../hooks/useLocale';

const CommentPage = () => {
  // route is now /publishing/:festschrift_id/review/:review_id
  //const { festschrift_id, review_id } = useParams();
  const { review_id } = useParams();

  const {
    pdfData,
    pdfError,
    reviewText,
    template,
    reviewStatus,
    reviewTemplateId,
    loadReview,
    article,
    loadPdfForVersion,
    updateReviewLocally,
  } = useReview(review_id);

  const [loading, setLoading] = useState(true);
  const [selectedVersionId, setSelectedVersionId] = useState(null); // --- Deadline (display only, one place: above versions) ---
  const [activeDeadline, setActiveDeadline] = useState(null);
  // Download menu for versions (DOCX/PDF)
  const [downloadMenuVersionId, setDownloadMenuVersionId] = useState(null);
  const downloadMenuRef = useRef(null);

  const [parties, setParties] = useState(null);

  const formatDeadlineDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    const opts = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return d.toLocaleString(lang === 'ru' ? 'ru-RU' : 'en-US', opts);
  };

  const getDeadlineLabelText = (deadline) => {
    if (!deadline?.due_at) return '';

    const role = (deadline.role || '').toLowerCase();

    const roleLabel =
      role === 'author'
        ? lang === 'ru'
          ? 'Дедлайн автора'
          : 'Author deadline'
        : role === 'reviewer'
          ? lang === 'ru'
            ? 'Дедлайн рецензента'
            : 'Reviewer deadline'
          : lang === 'ru'
            ? 'Дедлайн'
            : 'Deadline';

    const base = `${roleLabel}: ${formatDeadlineDateTime(deadline.due_at)}`;

    return deadline.overdue ? `${base} ${lang === 'ru' ? '(просрочено)' : '(overdue)'}` : base;
  };

  // ArticleDetailsDTO: article.id, article.text, article.article (with versions)
  const articleMeta = article?.article || {};
  const articleVersions = articleMeta?.versions || [];
  const currentArticleVersionId = articleMeta?.current_version_id || null;

  const articleStatus = (articleMeta?.status || '').toLowerCase();

  // We consider "ready + pdfData exists" as the moment when feedback is allowed.
  // (Prevents opening the review form while the work is still missing/not converted.)
  const canLeaveFeedback = articleStatus === 'ready' && !!pdfData && !pdfError;

  const getPdfPlaceholder = (langValue, statusValue, pdfErr, hasVersions) => {
    const st = (statusValue || '').toLowerCase();
    const errStatus = pdfErr?.status ?? null;

    // 410 = version exists but file path is broken/missing -> show error
    if (errStatus === 410) {
      return {
        showSpinner: false,
        text:
          langValue === 'ru'
            ? 'Произошла ошибка при загрузке PDF (файл отсутствует). Пожалуйста, сообщите организатору.'
            : 'An error occurred while loading the PDF (file is missing). Please contact the organizer.',
      };
    }

    // Any other non-404 error -> also error
    if (errStatus && errStatus !== 404) {
      return {
        showSpinner: false,
        text:
          langValue === 'ru'
            ? 'Произошла ошибка при загрузке PDF. Пожалуйста, сообщите организатору.'
            : 'An error occurred while loading the PDF. Please contact the organizer.',
      };
    }

    // Status-based failure (conversion failed)
    if (st === 'failed') {
      return {
        showSpinner: false,
        text:
          langValue === 'ru'
            ? 'Произошла ошибка при загрузке/конвертации работы. Пожалуйста, сообщите организатору.'
            : 'An error occurred while uploading/converting this article. Please contact the organizer.',
      };
    }

    // Upload/conversion in progress -> spinner
    if (st === 'uploaded' || st === 'converting') {
      return {
        showSpinner: true,
        text:
          langValue === 'ru'
            ? 'Работа загружается или конвертируется. Пожалуйста, подождите…'
            : 'The article is being uploaded or converted. Please wait…',
      };
    }

    // 404 cases:
    // - no versions => truly not uploaded
    // - versions exist => give “needs time” (spinner) instead of “not uploaded”
    if (errStatus === 404) {
      if (!hasVersions) {
        return {
          showSpinner: false,
          text:
            langValue === 'ru'
              ? 'Работа ещё не загружена.'
              : 'The article has not been uploaded yet.',
        };
      }
      return {
        showSpinner: true,
        text:
          langValue === 'ru'
            ? 'PDF ещё не готов. Пожалуйста, подождите…'
            : 'The PDF is not ready yet. Please wait…',
      };
    }

    // Default
    return {
      showSpinner: false,
      text:
        langValue === 'ru' ? 'Работа ещё не загружена.' : 'The article has not been uploaded yet.',
    };
  };

  const location = useLocation();
  const { user, participantId } = useContext(UserContext);

  const safeUserLabel = (lang) => {
    const ru =
      (user?.surname?.ru || user?.surname_ru || '') + ' ' + (user?.name?.ru || user?.name_ru || '');
    const en =
      (user?.surname?.en || user?.surname_en || '') + ' ' + (user?.name?.en || user?.name_en || '');
    const label = (lang === 'ru' ? ru : en).trim();
    return label || user?.email || user?.username || (lang === 'ru' ? 'Вы' : 'You');
  };

  const { getLocalizedFullName, getCurrentLanguage } = useLocale();
  const lang = getCurrentLanguage();
  const pdfPlaceholder = useMemo(
    () => getPdfPlaceholder(lang, articleStatus, pdfError, articleVersions.length > 0),
    [lang, articleStatus, pdfError?.status, articleVersions.length],
  );

  const searchParams = new URLSearchParams(location.search);

  // Итоговые статусы, при которых ревью и комментарии должны быть read-only
  const FINAL_REVIEW_STATUSES = ['approved', 'revision_required', 'rejected', 'finalized'];
  const isFinalStatus = FINAL_REVIEW_STATUSES.includes(reviewStatus);

  const editableFromQuery = searchParams.get('editable') === 'True';
  // Даже если в URL editable=True, финальный статус делает ревью/form read-only.
  // но смена статуса комментария должна оставаться доступной рецензенту.
  const editable = editableFromQuery && !isFinalStatus;

  // viewer: 'reviewer' (по умолчанию) или 'author' для read-only
  const viewerRole = (searchParams.get('viewer') || 'reviewer').toLowerCase();

  // - enableAdding: создание/удаление/редактирование треда (зависит от финального статуса)
  // - canChangeStatus: смена статуса (разрешена рецензенту даже после submit/finalize)
  const enableAdding = viewerRole === 'reviewer' && editable;
  const canChangeStatus = viewerRole === 'reviewer' && editableFromQuery;
  const reviewFormToggleDisabled = viewerRole === 'reviewer' && !canLeaveFeedback;
  const reviewFormToggleDisabledReason = pdfPlaceholder.text;

  // храним последний blob-URL, чтобы корректно его освобождать
  const lastPdfUrlRef = useRef(null);

  // при каждом новом pdfData:
  //  - если был старый blob-URL, освобождаем его
  //  - запоминаем новый
  useEffect(() => {
    if (pdfData && pdfData.startsWith('blob:')) {
      if (lastPdfUrlRef.current && lastPdfUrlRef.current !== pdfData) {
        try {
          URL.revokeObjectURL(lastPdfUrlRef.current);
        } catch (e) {
          console.warn('[CommentPage] revokeObjectURL error (old url):', e);
        }
      }
      lastPdfUrlRef.current = pdfData;
    }
  }, [pdfData]);

  // при размонтировании страницы освобождаем последний blob-URL
  useEffect(() => {
    return () => {
      if (lastPdfUrlRef.current) {
        try {
          URL.revokeObjectURL(lastPdfUrlRef.current);
        } catch (e) {
          console.warn('[CommentPage] revokeObjectURL error (unmount):', e);
        }
      }
    };
  }, []);

  // Close download menu on outside click / Esc
  useEffect(() => {
    const onMouseDown = (e) => {
      if (!downloadMenuVersionId) return;
      const el = downloadMenuRef.current;
      if (el && !el.contains(e.target)) {
        setDownloadMenuVersionId(null);
      }
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setDownloadMenuVersionId(null);
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [downloadMenuVersionId]);

  const getBreadcrumbsFromUrl = useCallback((search) => {
    const searchParams = new URLSearchParams(search);
    const flow = searchParams.get('flow');

    if (flow === 'dashboard') {
      const confName = searchParams.get('conferenceName');
      const thesisTitle = searchParams.get('thesisTitle');

      return [{ label: confName }, { label: thesisTitle }];
    }
    if (flow === 'management') {
      // still named conference_id/thesis_id in query, but they hold festschrift/article IDs
      const conference_id = searchParams.get('conference_id') || '';

      const confFromQuery = searchParams.get('conferenceName') || '';
      const confCached = (() => {
        try {
          return sessionStorage.getItem(`fest_name_${conference_id}`) || '';
        } catch {
          return '';
        }
      })();
      const confName = confFromQuery || confCached || '';

      const sectionName = searchParams.get('sectionName') || '';
      const thesisTitle = searchParams.get('thesisTitle') || '';

      const section_id = searchParams.get('section_id') || '';
      const thesis_id = searchParams.get('thesis_id') || '';

      const encConf = encodeURIComponent(confName);
      const encSection = encodeURIComponent(sectionName);
      const encThesis = encodeURIComponent(thesisTitle);

      return [
        {
          label: confName,
          url: `/management/publishing/${conference_id}?conferenceName=${encConf}`,
        },
        {
          label: sectionName,
          url: `/management/publishing/${conference_id}/sections/${section_id}/articles?conferenceName=${encConf}&sectionName=${encSection}`,
        },
        {
          label: thesisTitle,
          url: `/management/publishing/${conference_id}/sections/${section_id}/articles/${thesis_id}?conferenceName=${encConf}&sectionName=${encSection}&thesisTitle=${encThesis}`,
        },
      ];
    }
    return [];
  }, []);

  useUpdateBreadcrumbs(getBreadcrumbsFromUrl);

  const fetchDeadline = useCallback(async () => {
    if (!review_id) return;
    try {
      const res = await api.get(`/reviews/${review_id}/deadline`);
      setActiveDeadline(res.data || null);
    } catch (e) {
      console.warn('[CommentPage] Failed to fetch deadline', e);
      setActiveDeadline(null);
    }
  }, [review_id]);

  const fetchParties = useCallback(async () => {
    if (!review_id) return;
    try {
      const res = await api.get(`/reviews/${review_id}/parties`, {
        params: { viewer: viewerRole },
      });
      setParties(res.data || null);
    } catch (e) {
      console.warn('[CommentPage] Failed to fetch parties', e);
      setParties(null);
    }
  }, [review_id, viewerRole]);

  useEffect(() => {
    fetchDeadline();
    fetchParties();
  }, [fetchDeadline, fetchParties, reviewStatus, currentArticleVersionId]);

  const [comments, setComments] = useState(null);

  const formatVersionDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString();
    } catch {
      return String(iso);
    }
  };

  // Когда статья (и её версии) загрузились, выбираем версию по умолчанию
  useEffect(() => {
    const meta = article?.article || {};
    const versions = meta?.versions || [];
    const currentId = meta?.current_version_id || null;

    setSelectedVersionId((prev) => {
      // 1) если уже была выбрана и всё ещё существует - оставляем
      if (prev && versions.some((v) => v.id === prev)) {
        return prev;
      }
      // 2) иначе, если есть current_version_id - берём его
      if (currentId && versions.some((v) => v.id === currentId)) {
        return currentId;
      }
      // 3) иначе берём последнюю по списку
      if (versions.length > 0) {
        return versions[versions.length - 1].id;
      }
      // 4) версий нет
      return null;
    });
  }, [article]);

  // Request current comments from backend
  const fetchComments = useCallback(
    async (articleVersionId) => {
      try {
        setLoading(true);

        let url = `/comments/${review_id}`;
        if (articleVersionId != null) {
          const params = new URLSearchParams();
          params.set('article_version_id', articleVersionId);
          url += `?${params.toString()}`;
        }

        const response = await api.get(url);

        const commentsFromBackend = response.data || [];
        commentsFromBackend.forEach((comment) => {
          comment.thread = (comment.thread || []).map((m) => {
            const label = getLocalizedFullName(m.author_full_name);
            return {
              ...m,
              author: label && label !== 'not recognized' ? label : m.author || null,
            };
          });
        });
        setComments(commentsFromBackend);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [review_id, getLocalizedFullName],
  );

  // Загружаем комментарии, когда известна статья и (если есть версии) выбрана версия
  useEffect(() => {
    if (!review_id) return;
    if (!article) return; // ждём, пока loadReview подтянет статью

    const meta = article.article || {};
    const versions = meta.versions || [];

    if (versions.length === 0) {
      // Старый режим без версий статьи - подтягиваем все комментарии разом
      fetchComments(null);
      return;
    }

    // Если версии есть, но мы ещё не выбрали какую - ждём, пока выше выставит selectedVersionId
    if (selectedVersionId == null) {
      return;
    }

    // Нормальный случай: тянем комментарии только для выбранной версии
    fetchComments(selectedVersionId);
  }, [fetchComments, selectedVersionId, review_id, article]);

  // При смене выбранной версии загружаем соответствующий PDF
  useEffect(() => {
    const articleId = article?.id;
    if (!articleId) return;

    if (selectedVersionId != null) {
      // загружаем PDF конкретной версии
      loadPdfForVersion(articleId, selectedVersionId);
    } else {
      // если версий нет или не выбрано - используем то, что уже загрузил useReview (fallback)
    }
  }, [article, selectedVersionId, loadPdfForVersion]);

  const [selectedCommentId, setSelectedCommentId] = useState(null);
  const [rightWidth, setRightWidth] = useState(400);

  const containerRef = useRef(null);
  const pdfViewerRef = useRef(null);
  const commentListRef = useRef(null);

  const draggingRef = useRef(false);

  useEffect(() => {
    loadReview();
  }, [loadReview]);

  // --- Logic for dragging the separator ---
  const initialXRef = useRef(0);
  const initialRightWidthRef = useRef(400);

  const onDrag = useCallback((e) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - initialXRef.current;
    let newWidth = initialRightWidthRef.current - dx;
    const minWidth = 200;
    const containerWidth = containerRef.current ? containerRef.current.clientWidth : 0;
    const maxWidth = containerWidth - 200;
    if (newWidth < minWidth) newWidth = minWidth;
    if (newWidth > maxWidth) newWidth = maxWidth;
    setRightWidth(newWidth);
  }, []);

  const onStopDrag = useCallback(() => {
    draggingRef.current = false;
    window.removeEventListener('mousemove', onDrag);
    window.removeEventListener('mouseup', onStopDrag);
  }, [onDrag]);

  const startDrag = useCallback(
    (e) => {
      draggingRef.current = true;
      initialXRef.current = e.clientX;
      initialRightWidthRef.current = rightWidth;
      window.addEventListener('mousemove', onDrag);
      window.addEventListener('mouseup', onStopDrag);
    },
    [rightWidth, onDrag, onStopDrag],
  );

  // Global cleanup on unmounting (will run once)
  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', onDrag);
      window.removeEventListener('mouseup', onStopDrag);
    };
  }, [onDrag, onStopDrag]);

  // --- Обработчики событий между pdf и списком комментариев ---
  const onListCommentClicked = (commentId) => {
    setSelectedCommentId(commentId);
    if (pdfViewerRef.current && pdfViewerRef.current.scrollToComment) {
      setTimeout(() => {
        pdfViewerRef.current.scrollToComment(commentId);
      }, 100);
    }
  };

  const onPdfCommentSelected = (commentId) => {
    setSelectedCommentId(commentId);
    if (commentListRef.current && commentListRef.current.scrollToComment) {
      commentListRef.current.scrollToComment(commentId);
    }
  };

  const onCommentCreated = useCallback(
    async (newComment) => {
      const { title, position, page } = newComment;
      const encodedTitle = encodeURIComponent(title);

      try {
        let url = `/comments/${review_id}?participant_id=${participantId}&title=${encodedTitle}&page=${page}`;
        if (selectedVersionId != null) {
          url += `&article_version_id=${selectedVersionId}`;
        }

        const response = await api.post(url, position || null);

        if (response.status !== 200) {
          console.error('Error creating comment', response);
          return;
        }

        const createdComment = response.data;

        setComments((prev) => [...(prev || []), createdComment]);
        setSelectedCommentId(createdComment.id);

        setTimeout(() => {
          if (commentListRef.current?.scrollToComment) {
            commentListRef.current.scrollToComment(createdComment.id);
          }
          if (pdfViewerRef.current?.scrollToComment) {
            setTimeout(() => {
              pdfViewerRef.current.scrollToComment(createdComment.id);
            }, 100);
          }
        }, 0);
      } catch (e) {
        console.error('Error creating comment', e);
      }
    },
    [review_id, participantId, setComments, setSelectedCommentId, selectedVersionId, lang],
  );

  const onAppendReply = useCallback(
    async ({ commentId, reply }) => {
      const encodedReply = encodeURIComponent(reply);

      const serverResponse = await api.post(
        `/comments/${commentId}/message?participant_id=${participantId}&message=${encodedReply}`,
      );
      if (serverResponse.status !== 200) {
        return;
      }
      setComments((prev) =>
        prev.map((comment) => {
          if (comment.id === commentId) {
            const label = safeUserLabel(lang);
            return {
              ...comment,
              thread: [
                ...comment.thread,
                {
                  id: serverResponse.data.id,
                  text: reply,
                  time: new Date().toLocaleString(),
                  author: label,
                  author_full_name: null,
                  creatorId: participantId,
                },
              ],
            };
          }
          return comment;
        }),
      );
    },
    [participantId, setComments],
  );

  const ensureCommentsOpenAfterDecision = useCallback(
    async (nextStatus) => {
      // Only after a final decision from reviewer
      const isFinalDecision =
        nextStatus === 'approved' ||
        nextStatus === 'revision_required' ||
        nextStatus === 'rejected';

      if (viewerRole !== 'reviewer' || !isFinalDecision) return;

      const hasClosedComments = (comments || []).some((c) => c.visible_to_author === false);
      if (!hasClosedComments) return;

      try {
        let url = `/comments/${review_id}/open`;
        if (selectedVersionId != null) {
          const params = new URLSearchParams();
          params.set('article_version_id', selectedVersionId);
          url += `?${params.toString()}`;
        }

        const res = await api.post(url);
        if (res.status !== 200) return;

        setComments((prev) =>
          (prev || []).map((c) => ({
            ...c,
            visible_to_author: true,
          })),
        );
      } catch (e) {
        console.warn('[CommentPage] Failed to auto-open comments', e);
      }
    },
    [viewerRole, comments, review_id, selectedVersionId, setComments],
  );

  const onSaveReview = async (review, status) => {
    try {
      await saveReview(review_id, template.id, review, status);

      updateReviewLocally(status, review);
      await fetchDeadline();
      await ensureCommentsOpenAfterDecision(status);
      alert(lang === 'ru' ? 'Ревью сохранено!' : 'Review saved!');
    } catch (e) {
      console.error('Error saving review:', e);

      if (e?.response?.status === 403) {
        alert(
          lang === 'ru'
            ? 'Это ревью больше нельзя изменять для данной версии статьи.'
            : 'This review can no longer be edited for this article version.',
        );
      } else {
        alert(
          lang === 'ru'
            ? 'Не удалось сохранить ревью. Попробуйте ещё раз.'
            : 'Failed to save the review. Please try again.',
        );
      }
    }
  };

  const onDeleteComment = useCallback(
    async (commentId) => {
      try {
        const serverResponse = await api.delete(`/comments/${commentId}`);
        if (serverResponse.status !== 200) {
          console.error('Error deleting comment', serverResponse);
          return;
        }

        setComments((prev) => {
          const index = prev.findIndex((c) => c.id === commentId && c.creatorId === participantId);
          if (index !== -1) {
            const newComments = [...prev];
            newComments.splice(index, 1);
            return newComments;
          } else {
            console.warn(
              lang === 'ru'
                ? 'Комментарий не может быть удален (нет прав или не найден)'
                : 'Comment cannot be deleted (no rights or not found)',
              commentId,
            );
            return prev;
          }
        });
      } catch (error) {
        console.error(
          lang === 'ru' ? 'Ошибка при удалении комментария:' : 'Error deleting comment:',
          error,
        );
      }
    },
    [setComments],
  );

  const onDeleteReply = useCallback(
    async ({ commentId, replyIndex }) => {
      const currentComment = comments?.find((c) => c.id === commentId);
      if (!currentComment) {
        console.warn(
          lang === 'ru'
            ? 'Комментарий не найден для удаления ответа:'
            : 'Comment not found for deleting reply:',
          commentId,
        );
        return;
      }

      if (!currentComment.thread || !currentComment.thread[replyIndex]) {
        console.warn(
          lang === 'ru'
            ? 'Ответ не найден для удаления в комментарии:'
            : 'Reply not found for deletion in comment:',
          commentId,
        );
        return;
      }

      const reply = currentComment.thread[replyIndex];

      try {
        const serverResponse = await api.delete(`/comments/${commentId}/message/${reply.id}`);
        if (serverResponse.status !== 200) {
          console.error('Error deleting reply', serverResponse);
          return;
        }

        setComments((prev) =>
          prev.map((comment) => {
            if (comment.id === commentId) {
              if (
                comment.thread &&
                comment.thread[replyIndex] &&
                comment.thread[replyIndex].creatorId === participantId
              ) {
                const newThread = [...comment.thread];
                newThread.splice(replyIndex, 1);
                return {
                  ...comment,
                  thread: newThread,
                };
              }
            }
            return comment;
          }),
        );
      } catch (error) {
        console.error(
          lang === 'ru' ? 'Ошибка при удалении ответа:' : 'Error deleting reply:',
          error,
        );
      }
    },
    [comments, setComments],
  );

  const onChangeCommentStatus = async ({ commentId, newStatus }) => {
    try {
      const res = await api.patch(`/comments/${commentId}/status`, { status: newStatus });
      if (res.status !== 200) return;

      const updated = res.data;

      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? { ...c, status: updated.status } : c)),
      );
    } catch (e) {
      console.error('Failed to update comment status', e);
    }
  };

  const onOpenCommentsForAuthor = useCallback(async () => {
    try {
      let url = `/comments/${review_id}/open`;
      if (selectedVersionId != null) {
        const params = new URLSearchParams();
        params.set('article_version_id', selectedVersionId);
        url += `?${params.toString()}`;
      }

      const res = await api.post(url);
      if (res.status !== 200) {
        console.error(
          lang === 'ru'
            ? 'Ошибка при открытии комментариев автору'
            : 'Error opening comments for author',
          res,
        );
        return;
      }

      setComments((prev) =>
        (prev || []).map((c) => ({
          ...c,
          visible_to_author: true,
        })),
      );
    } catch (e) {
      console.error(
        lang === 'ru'
          ? 'Не удалось открыть комментарии для автора'
          : 'Failed to open comments for author',
        e,
      );
    }
  }, [review_id, selectedVersionId, setComments, lang]);

  const downloadArticleFile = useCallback(
    async ({ ext, versionId, versionIndex }) => {
      const articleId = article?.id;
      if (!articleId || !versionId) return;

      try {
        const ts = Date.now();
        const url = `/articles/${articleId}.${ext}?article_version_id=${versionId}&ts=${ts}`;

        const resp = await api.get(url, { responseType: 'blob' });

        const mime =
          ext === 'pdf'
            ? 'application/pdf'
            : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

        const blob = new Blob([resp.data], { type: mime });
        const href = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = href;

        const safeV = versionIndex != null ? `v${versionIndex}` : `v${versionId}`;
        a.download = `article_${articleId}_${safeV}.${ext}`;

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // cleanup
        setTimeout(() => {
          try {
            URL.revokeObjectURL(href);
          } catch {}
        }, 3000);
      } catch (e) {
        console.error('[CommentPage] download failed', e);
        const msg =
          lang === 'ru'
            ? 'Не удалось скачать файл. Попробуйте ещё раз. Если проблема сохранится - обратитесь к редактору'
            : 'Failed to download the file. Please try again. If the problem persist - contact the editor';
        alert(msg);
      }
    },
    [article?.id, lang],
  );

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner" />
        <div className="loading-text">
          {lang === 'ru' ? 'Загрузка страницы...' : 'Loading page...'}
        </div>
      </div>
    );
  }

  return (
    <div className="page-layout" ref={containerRef}>
      <div className="left-pane">
        <div className="left-pane-inner">
          {articleVersions.length > 0 && (
            <div className="article-versions">
              <div className="versions-header">
                {parties && (
                  <div className="comment-parties">
                    <div className="comment-party-row">
                      <span className="comment-party-label">
                        {lang === 'ru' ? 'Автор:' : 'Author:'}
                      </span>
                      <span className="comment-party-value">
                        {(() => {
                          const n = getLocalizedFullName(parties?.author?.full_name) || '';
                          const e = parties?.author?.email || '';
                          return (n || e || (lang === 'ru' ? 'Анонимно' : 'Anonymous')).trim();
                        })()}
                      </span>
                    </div>
                    <div className="comment-party-row">
                      <span className="comment-party-label">
                        {lang === 'ru' ? 'Рецензент:' : 'Reviewer:'}
                      </span>
                      <span className="comment-party-value">
                        {(() => {
                          const n = getLocalizedFullName(parties?.reviewer?.full_name) || '';
                          const e = parties?.reviewer?.email || '';
                          return (n || e || (lang === 'ru' ? 'Анонимно' : 'Anonymous')).trim();
                        })()}
                      </span>
                    </div>
                  </div>
                )}

                {activeDeadline?.due_at && (
                  <div
                    className={
                      'comment-deadline-label' + (activeDeadline.overdue ? ' overdue' : '')
                    }
                    title={getDeadlineLabelText(activeDeadline)}
                  >
                    {getDeadlineLabelText(activeDeadline)}
                  </div>
                )}
                <div className="versions-title">
                  {lang === 'ru' ? 'Версии статьи' : 'Article versions'}
                </div>
              </div>
              <div className="versions-list">
                {articleVersions.map((v) => {
                  const isSelected = v.id === selectedVersionId;
                  return (
                    <div key={v.id} className={'version-row' + (isSelected ? ' active' : '')}>
                      <button
                        type="button"
                        className={'version-pill' + (isSelected ? ' active' : '')}
                        onClick={() => {
                          setSelectedVersionId(v.id);
                          setDownloadMenuVersionId(null);
                        }}
                      >
                        <span className="version-main">
                          {lang === 'ru' ? 'Версия' : 'Version'} {v.version_index}
                          {v.is_current && (
                            <span className="tag-current">
                              {lang === 'ru' ? 'Текущая' : 'Current'}
                            </span>
                          )}
                        </span>
                        <span className="version-meta">
                          {v.created_at
                            ? lang === 'ru'
                              ? `Загружена ${formatVersionDate(v.created_at)}`
                              : `Uploaded ${formatVersionDate(v.created_at)}`
                            : lang === 'ru'
                              ? 'Дата не указана'
                              : 'No date'}
                        </span>
                      </button>

                      <button
                        type="button"
                        className="version-download-btn"
                        title={lang === 'ru' ? 'Скачать' : 'Download'}
                        aria-label={lang === 'ru' ? 'Скачать' : 'Download'}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDownloadMenuVersionId((prev) => (prev === v.id ? null : v.id));
                        }}
                      >
                        ⭳
                      </button>

                      {downloadMenuVersionId === v.id && (
                        <div className="version-download-menu" ref={downloadMenuRef}>
                          <button
                            type="button"
                            className="version-download-item"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDownloadMenuVersionId(null);
                              downloadArticleFile({
                                ext: 'docx',
                                versionId: v.id,
                                versionIndex: v.version_index,
                              });
                            }}
                          >
                            {lang === 'ru' ? 'Скачать DOCX' : 'Download DOCX'}
                          </button>
                          <button
                            type="button"
                            className="version-download-item"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDownloadMenuVersionId(null);
                              downloadArticleFile({
                                ext: 'pdf',
                                versionId: v.id,
                                versionIndex: v.version_index,
                              });
                            }}
                          >
                            {lang === 'ru' ? 'Скачать PDF' : 'Download PDF'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="pdf-pane">
            <div className="commentpage-pdf-help">
              <HelpButton helpId="comment.page.pdf" helpLabel="Подсказка" />
            </div>
            {pdfData ? (
              <PdfViewerWithComments
                ref={pdfViewerRef}
                pdfBlobUrl={pdfData}
                comments={comments}
                currentUserId={participantId}
                onCommentSelected={onPdfCommentSelected}
                onCommentCreated={enableAdding ? onCommentCreated : undefined}
                editable={enableAdding}
              />
            ) : (
              <div className="pdf-loading">
                {pdfPlaceholder.showSpinner && <div className="spinner" />}
                <div className="pdf-loading-text">{pdfPlaceholder.text}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className="divider"
        onMouseDown={(e) => {
          e.preventDefault();
          startDrag(e);
        }}
      />

      <div className="right-pane" style={{ width: `${rightWidth}px` }}>
        <div className="commentpage-form-help">
          <HelpButton helpId="comment.page.review_form" helpLabel="Подсказка" />
        </div>
        <CommentList
          ref={commentListRef}
          comments={comments}
          selectedCommentId={selectedCommentId}
          currentParticipantId={participantId}
          onCommentClicked={onListCommentClicked}
          onAppendReply={onAppendReply}
          onDeleteComment={onDeleteComment}
          onDeleteReply={onDeleteReply}
          onChangeCommentStatus={onChangeCommentStatus}
          onSaveReview={onSaveReview}
          onOpenCommentsForAuthor={onOpenCommentsForAuthor}
          template={template}
          currentFormValues={template?.id === reviewTemplateId ? reviewText : {}}
          currentStatus={reviewStatus}
          commentsEditable={enableAdding}
          canChangeStatus={canChangeStatus}
          allowReplies={(viewerRole === 'author') | (viewerRole === 'reviewer')}
          formEditable={enableAdding}
          viewerRole={viewerRole}
          reviewFormToggleDisabled={reviewFormToggleDisabled}
          reviewFormToggleDisabledReason={reviewFormToggleDisabledReason}
        />
      </div>
    </div>
  );
};

export default CommentPage;
